import { EVENT_YEAR_MIN, GUNDEM_FUTURE_TOLERANCE_DAYS, GUNDEM_MAX_AGE_DAYS, type Realm } from "./constants.js";

export type RealmErrorCode =
  | "EVENT_DATE_REQUIRED"
  | "EVENT_DATE_INVALID"
  | "EVENT_DATE_OUT_OF_RANGE"
  | "EVENT_YEAR_REQUIRED"
  | "EVENT_YEAR_INVALID"
  | "EVENT_YEAR_OUT_OF_RANGE"
  | "REALM_FIELD_NOT_ALLOWED";

export interface RealmFields {
  event_date?: string | null | undefined;
  event_year?: number | null | undefined;
}

export type RealmResult =
  | { ok: true; realm: Realm; eventDate: string | null; eventYear: number | null }
  | { ok: false; code: RealmErrorCode; message: string };

const MESSAGES: Record<RealmErrorCode, string> = {
  EVENT_DATE_REQUIRED: "gundem bulgusu event_date (YYYY-AA-GG) gerektirir.",
  EVENT_DATE_INVALID: "event_date geçerli bir takvim günü olmalı (YYYY-AA-GG).",
  EVENT_DATE_OUT_OF_RANGE: `event_date son ${GUNDEM_MAX_AGE_DAYS} gün içinde olmalı.`,
  EVENT_YEAR_REQUIRED: "tarih bulgusu event_year (tam sayı, MÖ için negatif) gerektirir.",
  EVENT_YEAR_INVALID: "event_year sıfırdan farklı bir tam sayı olmalı (MÖ için negatif).",
  EVENT_YEAR_OUT_OF_RANGE: `event_year ${EVENT_YEAR_MIN} ile bu yıl arasında olmalı.`,
  REALM_FIELD_NOT_ALLOWED: "gundem bulgusunda event_year, tarih bulgusunda event_date gönderilmez.",
};

export function realmErrorMessage(code: RealmErrorCode): string {
  return MESSAGES[code];
}

const DAY_MS = 86_400_000;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Days since the Unix epoch of a valid "YYYY-MM-DD" calendar date, or null. */
export function parseEventDate(value: string): number | null {
  const m = DATE_RE.exec(value);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const ms = Date.UTC(y, mo - 1, d);
  const back = new Date(ms);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null;
  return Math.floor(ms / DAY_MS);
}

/**
 * Realm rules of a topic (spec "İki alanın içerik ayrımı"):
 *  - gundem: event_date required, a real calendar day between
 *    today − maxAgeDays (owner setting, default GUNDEM_MAX_AGE_DAYS) and today + GUNDEM_FUTURE_TOLERANCE_DAYS (UTC days, server clock);
 *    event_year not allowed.
 *  - tarih: event_year required, a non-zero integer in [EVENT_YEAR_MIN, current UTC year]
 *    (negative = BCE); event_date not allowed.
 */
export function validateRealmFields(
  realm: Realm,
  fields: RealmFields,
  nowMs: number,
  maxAgeDays: number = GUNDEM_MAX_AGE_DAYS,
): RealmResult {
  const fail = (code: RealmErrorCode): RealmResult => ({
    ok: false,
    code,
    message: code === "EVENT_DATE_OUT_OF_RANGE" ? `event_date son ${maxAgeDays} gün içinde olmalı.` : MESSAGES[code],
  });
  const hasDate = fields.event_date !== undefined && fields.event_date !== null;
  const hasYear = fields.event_year !== undefined && fields.event_year !== null;

  if (realm === "gundem") {
    if (hasYear) return fail("REALM_FIELD_NOT_ALLOWED");
    if (!hasDate) return fail("EVENT_DATE_REQUIRED");
    const day = parseEventDate(fields.event_date as string);
    if (day === null) return fail("EVENT_DATE_INVALID");
    const today = Math.floor(nowMs / DAY_MS);
    if (day < today - maxAgeDays || day > today + GUNDEM_FUTURE_TOLERANCE_DAYS) return fail("EVENT_DATE_OUT_OF_RANGE");
    return { ok: true, realm, eventDate: fields.event_date as string, eventYear: null };
  }

  if (hasDate) return fail("REALM_FIELD_NOT_ALLOWED");
  if (!hasYear) return fail("EVENT_YEAR_REQUIRED");
  const year = fields.event_year as number;
  if (!Number.isInteger(year) || year === 0) return fail("EVENT_YEAR_INVALID");
  if (year < EVENT_YEAR_MIN || year > new Date(nowMs).getUTCFullYear()) return fail("EVENT_YEAR_OUT_OF_RANGE");
  return { ok: true, realm, eventDate: null, eventYear: year };
}

/** Display form of an event year: -213 → "MÖ 213", 79 → "MS 79", 1912 → "1912". */
export function formatEventYear(year: number): string {
  if (year < 0) return `MÖ ${-year}`;
  if (year < 1000) return `MS ${year}`;
  return String(year);
}

/** Today's UTC calendar date as "YYYY-MM-DD" (agents use it as a default event_date). */
export function utcDateString(nowMs: number): string {
  return new Date(nowMs).toISOString().slice(0, 10);
}
